import { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TEXTS = {
  bg: {
    title: 'Само за изследователски и образователни цели',
    body: 'InkPhD е AI-асистирана платформа за академично писане и работа с литература. Платформата НЕ предоставя медицинска диагноза, препоръки за лечение или клинично подпомагане на решения. Всяко генерирано научно съдържание трябва да бъде независимо прегледано и валидирано от квалифицирани изследователи, научни ръководители и автори преди публикуване или клинично прилагане.',
    dismiss: 'Разбрах',
  },
  en: {
    title: 'For research and educational purposes only',
    body: 'InkPhD is an AI-assisted academic writing and literature organization platform. The platform does NOT provide medical diagnosis, treatment recommendations, or clinical decision support. All generated scientific content must be independently reviewed and validated by qualified researchers, supervisors, and authors before publication or clinical use.',
    dismiss: 'I understand',
  },
};

/**
 * Global research-use-only disclaimer banner.
 *
 * Variants:
 *  - "banner" — full banner with dismiss (top of page)
 *  - "inline" — slim inline strip (no dismiss)
 *  - "modal" — for export confirmation (always visible)
 */
export default function LegalDisclaimer({ variant = 'banner', lang = 'en', persistKey = 'inkphd_disclaimer_v1' }) {
  // Platform UI is English-only; `lang` prop preserved for backward compat but force EN.
  void lang;
  const [dismissed, setDismissed] = useState(false);
  const t = TEXTS.en;

  useEffect(() => {
    if (variant === 'banner') {
      try {
        if (localStorage.getItem(persistKey) === '1') setDismissed(true);
      } catch (e) { /* ignore */ }
    }
  }, [variant, persistKey]);

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(persistKey, '1'); } catch (e) { /* ignore */ }
  };

  if (variant === 'banner' && dismissed) return null;

  if (variant === 'inline') {
    return (
      <div className="text-[11px] leading-relaxed text-muted-foreground bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/40 dark:border-amber-900/40 rounded-md px-3 py-2 flex items-start gap-2" data-testid="legal-disclaimer-inline">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
        <span><strong>{t.title}.</strong> {t.body}</span>
      </div>
    );
  }

  if (variant === 'modal') {
    return (
      <div className="rounded-lg border border-amber-300/60 dark:border-amber-800/60 bg-amber-50/70 dark:bg-amber-950/40 p-4" data-testid="legal-disclaimer-modal">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
          <div>
            <h4 className="font-semibold text-sm mb-1">{t.title}</h4>
            <p className="text-xs leading-relaxed text-foreground/80">{t.body}</p>
          </div>
        </div>
      </div>
    );
  }

  // banner variant (default)
  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border-b border-amber-200/60 dark:border-amber-900/40" data-testid="legal-disclaimer-banner">
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700 dark:text-amber-400" />
        <p className="text-xs leading-relaxed text-foreground/85 flex-1">
          <strong className="font-semibold">{t.title}.</strong> {t.body}
        </p>
        <Button variant="ghost" size="sm" onClick={handleDismiss} className="h-6 px-2 text-xs" data-testid="legal-disclaimer-dismiss">
          {t.dismiss} <X className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}
